import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {useDropzone} from 'react-dropzone';
import { useFeathers } from '../../app/util';
import Button from '@mui/material/Button';
import { IconButton, LinearProgress, Link, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
/* import MUI checks */
import { Cancel, CheckCircle as GreenCheck, InfoOutlined, Replay } from '@mui/icons-material';
import { CircularProgress as LoadingSpinner } from '@mui/material';
import superagent from 'superagent';

/*
*  This component accepts a .zip file, uploads it to AWS, calls the lambda to process the data
*  and then returns the processed data to the parent component.
*/

const ShapefileUpload = ({
  onChangeShapefiles,
  style = {},
  uploadScope,
  activeUploadJob,
  onUploadJobChange,
  onNext,
  metadataReadOnly = false,
  showVotingTypeConfig = false,
}) => {
  const POLL_SELECT_FIELDS = [
    '_id',
    'key',
    'bucket',
    'status',
    'error',
    'totalRows',
    'loadedRows',
    'percent',
    'geoDistrictCreationStatus',
    'districtCreationStatus',
    'districtCreationPercent',
    'districtCreationProcessed',
    'districtCreationTotal',
    'geoDistrictCreationPercent',
    'geoDistrictCreationProcessed',
    'geoDistrictCreationTotal',
    'source',
    'expectedExpiration',
    'votingType'
  ];
  const hiddenFileInput = useRef(null);
  const [ isUploading, setIsUploading ] = useState(false);
  const [ shapefileLocation, setShapefileLocation ] = useState(null);
  const [ processingStatusMessage, setProcessingStatusMessage ] = useState(null);
  const [ processingProgress, setProcessingProgress ] = useState(0);
  const [ currentUploadJob, setCurrentUploadJob ] = useState(null);
  const [ shapefileData, setShapefileData ] = useState(false);
  const [ polling, setPolling ] = useState(false);
  const [ error, setError ] = useState(null);
  const [ source, setSource ] = useState('');
  const [ expectedExpiration, setExpectedExpiration ] = useState(() => defaultExpectedExpiration());
  const [ votingType, setVotingType ] = useState('voting-district');
  const [ savingMetadata, setSavingMetadata ] = useState(false);
  const lastEmittedRowsForJob = useRef(null);
  const lastMetadataJobId = useRef(null);
  const feathers = useFeathers();

  function defaultExpectedExpiration() {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 2);
    return formatDateInputValue(date);
  }

  function formatDateInputValue(dateLike) {
    if(!dateLike) return '';
    const date = new Date(dateLike);
    if(Number.isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
  }

  function formatDisplayDate(dateLike) {
    if(!dateLike) return '';
    const date = new Date(dateLike);
    if(Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString();
  }

  function isHttpUrl(value) {
    return /^https?:\/\//i.test((value || '').trim());
  }

  const handleClick = () => {
    if(hiddenFileInput?.current)
      hiddenFileInput.current.click();
  };

  const onDrop = (inputs) => fileSelected(null, inputs[0]);

  const mapRows = (rows = []) => {
    return rows.map((row, idx) => ({
      dataIndex: typeof row?.dataIndex === 'number' ? row.dataIndex : idx,
      ...row
    }));
  };

  const clearUploadState = () => {
    setShapefileLocation(null);
    setProcessingProgress(0);
    setProcessingStatusMessage(null);
    setCurrentUploadJob(null);
    setShapefileData(null);
    setError(null);
    setPolling(false);
    lastEmittedRowsForJob.current = null;
    lastMetadataJobId.current = null;
    setSource('');
    setExpectedExpiration(defaultExpectedExpiration());
    setVotingType('voting-district');
    onChangeShapefiles([]);
    onUploadJobChange?.(null);
  };

  const restartUpload = async () => {
    if(currentUploadJob?._id && feathers) {
      try {
        await feathers.getService('shapefiles').patch(currentUploadJob._id, { $cancel: true });
      } catch (err) {
        console.error(err);
        setError(err);
        return;
      }
    }

    clearUploadState();
  };

  const syncWithJob = (job) => {
    if(!job) {
      setCurrentUploadJob(null);
      setPolling(false);
      return;
    }

    setCurrentUploadJob(job);
    onUploadJobChange?.(job);
    if(lastMetadataJobId.current !== `${job._id}`) {
      lastMetadataJobId.current = `${job._id}`;
      if(job.source) {
        setSource(job.source);
      }
      if(job.expectedExpiration) {
        setExpectedExpiration(formatDateInputValue(job.expectedExpiration));
      }
      setVotingType(job.votingType || 'voting-district');
    }
    const loadedRows = job.loadedRows || 0;
    const totalRows = job.totalRows || 0;
    const progress = typeof job.percent === 'number'
      ? job.percent
      : (totalRows > 0 ? Math.round((loadedRows / totalRows) * 100) : 0);
    setProcessingProgress(progress);

    if(job.geoDistrictCreationStatus === 'in-progress' && job.districtCreationStatus === 'in-progress') {
      const districtCreationProcessed = job.districtCreationProcessed || 0;
      const districtCreationTotal = job.districtCreationTotal || 0;
      setProcessingStatusMessage(`Creating districts ${districtCreationProcessed} of ${districtCreationTotal || '?'}.`);
      setProcessingProgress(typeof job.districtCreationPercent === 'number' ? job.districtCreationPercent : progress);
      setPolling(true);
    } else if(job.geoDistrictCreationStatus === 'in-progress') {
      const creationProcessed = job.geoDistrictCreationProcessed || 0;
      const creationTotal = job.geoDistrictCreationTotal || 0;
      setProcessingStatusMessage(`Creating geodistricts ${creationProcessed} of ${creationTotal || '?'}.`);
      setProcessingProgress(typeof job.geoDistrictCreationPercent === 'number' ? job.geoDistrictCreationPercent : progress);
      setPolling(true);
    } else if(job.status === 'in-progress') {
      setProcessingStatusMessage(`${loadedRows} of ${totalRows || '?'} loaded.`);
      setPolling(true);
    } else if(job.status === 'processed') {
      const mappedRows = mapRows(job.rows || []);
      if(lastEmittedRowsForJob.current !== `${job._id}`) {
        onChangeShapefiles(mappedRows);
        lastEmittedRowsForJob.current = `${job._id}`;
      }
      setShapefileData({
        total: job.totalRows || mappedRows.length,
        data: mappedRows
      });
      setProcessingStatusMessage(`Processed shapefile. ${mappedRows.length} districts found.`);
      setProcessingProgress(100);
      setPolling(false);
    } else if(job.status === 'error') {
      const errorMessage = job?.error?.message || 'Shapefile processing failed.';
      setError(new Error(errorMessage));
      setProcessingStatusMessage(errorMessage);
      setPolling(false);
    } else if(job.status === 'canceled') {
      setProcessingStatusMessage('Upload canceled.');
      setPolling(false);
    } else if(job.status === 'used') {
      setProcessingStatusMessage('Upload already used.');
      setPolling(false);
    }
  };

  const pollJob = async (jobId) => {
    if(!feathers || !jobId) return;
    const nextJob = await feathers.getService('shapefiles').get(jobId, {
      query: {
        $includeRows: false,
        $select: POLL_SELECT_FIELDS
      }
    });
    const shouldLoadRows = ['processed', 'used'].includes(nextJob?.status) && !Array.isArray(nextJob?.rows);
    if(shouldLoadRows) {
      const nextJobWithRows = await feathers.getService('shapefiles').get(jobId, {
        query: {
          $includeGeometry: true
        }
      });
      syncWithJob(nextJobWithRows);
      return;
    }
    syncWithJob(nextJob);
  };

  const cancelUpload = async () => {
    if(!feathers || !currentUploadJob?._id) return;
    const canceledJob = await feathers.getService('shapefiles').patch(currentUploadJob._id, { $cancel: true });
    syncWithJob(canceledJob);
  };

  useEffect(() => {
    if(!activeUploadJob?._id) {
      return;
    }
    syncWithJob(activeUploadJob);
  }, [activeUploadJob]);

  useEffect(() => {
    if(!polling || !currentUploadJob?._id || !feathers) {
      return;
    }
    const pollInterval = window.setInterval(() => {
      pollJob(currentUploadJob._id).catch((err) => {
        console.error(err);
        setError(err);
      });
    }, 2000);

    return () => {
      window.clearInterval(pollInterval);
    };
  }, [polling, currentUploadJob?._id, feathers]);

  const fileSelected = async (event, fileGiven) => {
    const file = fileGiven || event?.target?.files?.[0];
    if(event?.target) {
      event.target.value = '';
    }
    if(!file) return;

    setIsUploading(true);
    setError(null);
    setShapefileLocation(null);
    setProcessingProgress(0);
    setProcessingStatusMessage(null);
    setCurrentUploadJob(null);
    setPolling(false);
    setShapefileData(null);
    lastEmittedRowsForJob.current = null;
    onChangeShapefiles([]);
    onUploadJobChange?.(null);

    try {
      if(!feathers) return;
      if(!uploadScope?.stateCode || !uploadScope?.tier2Type) {
        throw new Error('Missing upload scope data. Please reopen this modal and try again.');
      }

      const { accessToken } = await feathers.get('authentication');
      const url = process.env.NODE_ENV === 'development'
          ? 'http://localhost:3030/api/v1/uploads'
          : 'https://www.branch.vote/api/v1/uploads';

      const uploadResult = (
        await superagent.post(url)
          .set('Authorization', accessToken)
          .query({ type: 'shapefiles'})
          .attach('file', file)
      ).body

      setIsUploading(false)
      setShapefileLocation(uploadResult);
      setProcessingProgress(0);
      const createdJob = await feathers.getService('shapefiles').create({
        key: uploadResult.key,
        bucket: uploadResult.bucket,
        stateCode: uploadScope.stateCode,
        tier2Type: uploadScope.tier2Type,
        tier2DistrictId: uploadScope.tier2DistrictId
      });
      syncWithJob(createdJob);
    } catch (err) {
      console.error(err)
      setError(err)
      setShapefileLocation(null);
      setShapefileData(null);
      setIsUploading(false);
    } finally {
      setIsUploading(false);
    }
  };

  const saveMetadataAndContinue = async () => {
    if(!feathers || !currentUploadJob?._id) return;
    if(!source.trim() || !expectedExpiration) {
      setError(new Error('Source and expected expiration are required.'));
      return;
    }

    setSavingMetadata(true);
    setError(null);
    try {
      const metadataPayload = {
        source: source.trim(),
        expectedExpiration
      };
      if(showVotingTypeConfig) {
        metadataPayload.votingType = votingType;
      }
      const updatedJob = await feathers.getService('shapefiles').patch(currentUploadJob._id, metadataPayload);
      const jobForNext = ['processed', 'used'].includes(updatedJob?.status)
        ? await feathers.getService('shapefiles').get(updatedJob._id, {
          query: {
            $includeGeometry: true
          }
        })
        : updatedJob;
      syncWithJob(jobForNext);
      onNext?.(jobForNext);
    } catch (err) {
      console.error(err);
      setError(err);
    } finally {
      setSavingMetadata(false);
    }
  };

  const sourceSaved = Boolean(currentUploadJob?.source);
  const expirationSaved = Boolean(currentUploadJob?.expectedExpiration);
  const savedVotingType = currentUploadJob?.votingType || votingType || 'voting-district';
  const hasUploadedShapefile = Boolean(
    currentUploadJob?._id
    && currentUploadJob?.key
    && currentUploadJob?.bucket
    && !['canceled', 'error'].includes(currentUploadJob?.status)
  );
  const metadataComplete = Boolean(source.trim()) && Boolean(expectedExpiration) && hasUploadedShapefile;
  const metadataFields = metadataReadOnly ? (
    <MetadataDisplay>
      {
        sourceSaved &&
        <Typography variant='body2'>
          Source:{' '}
          {
            isHttpUrl(currentUploadJob.source)
              ? <Link href={currentUploadJob.source} target="_blank" rel="noreferrer">{currentUploadJob.source}</Link>
              : currentUploadJob.source
          }
        </Typography>
      }
      {
        expirationSaved &&
        <Typography variant='body2'>
          Expected expiration: {formatDisplayDate(currentUploadJob.expectedExpiration)}
        </Typography>
      }
      {
        showVotingTypeConfig &&
        <Typography variant='body2'>
          At large?: {savedVotingType === 'representative-district' ? 'Yes' : 'No'}
        </Typography>
      }
    </MetadataDisplay>
  ) : (
    <MetadataWrapper>
      <MetadataInputRow>
        <Typography variant='body1'>Source</Typography>
        <TextField
          value={source}
          onChange={(event) => setSource(event.target.value)}
          placeholder="https://example.com/source"
          size="small"
          fullWidth
        />
      </MetadataInputRow>
      <MetadataInputRow>
        <MetadataLabel>
          <Typography variant='body1'>Expected expiration</Typography>
          <Tooltip title='Most districts are expected to be redrawn within 2–3 years of the census. When uncertain, select the earliest likely date.'>
            <InfoOutlined style={{ fontSize: '16px' }} color='action' />
          </Tooltip>
        </MetadataLabel>
        <TextField
          type="date"
          value={expectedExpiration}
          onChange={(event) => setExpectedExpiration(event.target.value)}
          size="small"
          fullWidth
        />
      </MetadataInputRow>
      {
        showVotingTypeConfig &&
        <MetadataInputRow>
          <Typography variant='body1'>At large?</Typography>
          <TextField
            select
            value={votingType}
            onChange={(event) => setVotingType(event.target.value)}
            size="small"
            fullWidth
          >
            <MenuItem value="voting-district">No</MenuItem>
            <MenuItem value="representative-district">Yes</MenuItem>
          </TextField>
        </MetadataInputRow>
      }
      <Button
        variant="contained"
        color="primary"
        onClick={saveMetadataAndContinue}
        disabled={!metadataComplete || savingMetadata}
        style={{ alignSelf: 'flex-end' }}
      >
        {savingMetadata ? 'Saving...' : 'Next'}
      </Button>
    </MetadataWrapper>
  );

  const accept = '.zip';

  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    onDrop,
    accept,
    noClick: true,
    maxFiles: 1
  })


  if(shapefileLocation || shapefileData || isUploading || currentUploadJob) {
    const inProgress = currentUploadJob?.status === 'in-progress';
    const showProgress = inProgress || processingProgress > 0;
    const canReplaceUpload = !inProgress && Boolean(currentUploadJob);

    return (
      <Wrapper style={{ display: 'flex', gap: '12px', ...style }}>
        {
          isUploading &&
          <Row>
            <LoadingSpinner />
            <Typography variant='body1'>Uploading shapefiles...</Typography>
          </Row>
        }
        {
          !isUploading && <>
            {
              shapefileLocation && !shapefileData &&
              <Row>
                <GreenCheck style={{ fontSize: '16px' }} />
                <Typography variant='body2'>Uploaded shapefile to {shapefileLocation.key}</Typography>
              </Row>
            }
            {
              shapefileData
              ?
                <>
                  <Row style={{ width: '100%', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <GreenCheck style={{ fontSize: '16px' }} />
                      <Typography variant='body2'>Processed shapefile. {shapefileData.total} districts found.</Typography>
                    </div>
                    {
                      !inProgress &&
                      <ActionButtons>
                        <Tooltip title='Restart'>
                          <IconButton size='small' color='primary' onClick={restartUpload}>
                            <Cancel fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </ActionButtons>
                    }
                  </Row>
                  {metadataFields}
                </>
              :
                <>
                  <Row style={{ width: '100%', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {
                        inProgress
                        ? <LoadingSpinner />
                        : <GreenCheck style={{ fontSize: '16px' }} />
                      }
                      <Typography variant='body1'>{processingStatusMessage || 'Processing shapefile...'}</Typography>
                    </div>
                    {
                      inProgress
                      ? (
                        <Button variant='outlined' color='inherit' onClick={cancelUpload}>
                          Cancel
                        </Button>
                      )
                      : (
                        canReplaceUpload &&
                        <ActionButtons>
                          <Tooltip title='Restart'>
                            <IconButton size='small' color='primary' onClick={restartUpload}>
                              <Cancel fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        </ActionButtons>
                      )
                    }
                  </Row>
                  {
                    showProgress &&
                    <ProgressWrapper>
                      <LinearProgress variant='determinate' value={processingProgress} />
                      <Typography variant='caption'>{processingProgress}%</Typography>
                    </ProgressWrapper>
                  }
                  {
                    currentUploadJob?.status === 'error' && error?.message &&
                    <Typography variant='body2' color='error'>
                      {error.message}
                    </Typography>
                  }
                  {metadataFields}
                </>
            }
          </>
        }
        <input type="file"
          {...getInputProps()}
          ref={hiddenFileInput}
          onChange={fileSelected}
          style={{ display:'none' }}
          accept={accept}
        />
      </Wrapper>
    )
  }


  return (
    <Wrapper className='upload-button-wrapper'
      {...getRootProps()}
      isDragActive={isDragActive ? 1 : 0}
      style={style}
    >
      {
        error &&
        <Row>
          <Typography variant='body1' color='error'>{error.message}</Typography>
        </Row>
      }
      <UploadRow>
        <Typography variant='body1'>Upload the .zip shapefile</Typography>
        <Button variant='outlined' color='primary' onClick={handleClick}>
          { isUploading ? 'LOADING...' : 'UPLOAD'}
        </Button>
      </UploadRow>
      {metadataFields}
      <input type="file"
        {...getInputProps()}
        ref={hiddenFileInput}
        onChange={fileSelected}
        style={{ display:'none' }}
        accept={accept}
      />  
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: calc(100% - 14px * 2);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${props => props.isDragActive
  ?`
    border: dashed 2px #CCCCCC;
    background-color: #EEEEEE;
    padding: 16px;
  `
  :`
    border: solid: 1px #EEEEEE;
  `}
`

const Row = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`

const UploadRow = styled(Row)`
    justify-content: space-between;
    width: 100%;
    max-width: 640px;
`

const ActionButtons = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const ProgressWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
    max-width: 640px;
`

const MetadataWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    max-width: 640px;
`

const MetadataInputRow = styled.div`
    display: grid;
    grid-template-columns: 180px 1fr;
    align-items: center;
    gap: 16px;
`

const MetadataLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`

const MetadataDisplay = styled.div`
    display: flex;
    flex-direction: row;
    gap: 16px;
    margin-top: -8px;
`

export { ShapefileUpload };
